import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { useAlertDialog } from '@/providers/AlertDialog.provider';

import { useClientData } from '@/providers/ClientData.provider';
import { PaymentFields, PaymentFieldDefault } from '../../../hooks/useFields/TransactionCreationFields/types';
import SingleCollectionForm from './SingleCollectionForm';

import usePostInteracMoneyRequest from '@/hooks/useApi/usePostInteracMoneyRequest';
import useWithdrawMyAccount from '@/hooks/useApi/useWithdrawMyAccount';
import usePostEftFund from '@/hooks/useApi/usePostEftFund';
import usePostIQ11Tokenize from '@/hooks/useApi/usePostIQ11Tokenize';
import useCreateScheduledPayment from '@/hooks/useApi/useCreateScheduledPayment';
import usePostACHFund from '@/hooks/useApi/usePostACHFund';
import usePostEmbedUrl from '@/hooks/useApi/usePostEmbedUrl';
import usePaymentMethodCurrencySupport from '@/hooks/useFields/TransactionCreationFields/usePaymentMethodCurrencySupport';
import usePostSendPaylink from '@/hooks/useApi/usePostSendPaylink';

import {
  Box,
  IsTodayDate,
  UtilButton,
  withStyles,
  allCardBrands,
  toSupportedCurrency,
  ScheduledPaymentRail,
} from 'vopay';
import { Title } from 'UI';
import useContacts from '@/hooks/useApi/useContacts';
import useClientAccounts from '@/hooks/useApi/useClientAccounts';
import { useTranslation } from 'react-i18next';
import { useBlockingLoader } from 'shared';
import usePaymentMethods from '@/hooks/usePaymentMethods';
import useUploadDocument from '@/hooks/useApi/useUploadDocument';
import usePostChargeCreditCard from '@/hooks/useApi/usePostChargeCreditCard';
import useDebitCard from '@/hooks/useApi/useDebitCard';
import { useHandlePaymentMethodChange } from '@/hooks/useFields/TransactionCreationFields';
import useFetchContactPaymentMethod from '@/hooks/useApi/useFetchContactPaymentMethod';
import styles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';
import useELinxConnect from '@/hooks/useELinxConnect';

import { useDataDog } from '@/providers/Datadog.provider';

const classes = withStyles(styles);

type TokenProps = {
  token: string;
  contact: string;
};

const SingleCollectionContainer: FunctionComponent = () => {
  const { state } = useLocation<TokenProps>();
  const history = useHistory();
  const eLinxConnect = useELinxConnect();
  const contact = state?.contact || '';
  const token = state?.token || '';
  const { logMessage } = useDataDog();

  const {
    response: { clientAccountsEnabled, Country, AccountID, AccountName, LoginUserName },
  } = useClientData();

  const { t } = useTranslation('translations', { keyPrefix: 'SingleCollection' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const handlePaymentMethodChange = useHandlePaymentMethodChange();
  const { fetchPaymentMethods } = useFetchContactPaymentMethod();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<Partial<PaymentFieldDefault> | null>(null);
  const blockingLoader = useBlockingLoader();

  useEffect(() => {
    if (!contact || !token) {
      setSelectedPaymentMethod({});

      return;
    }

    const fetchPaymentMethod = async () => {
      try {
        blockingLoader.show();
        const paymentMethodsResponse = await fetchPaymentMethods(state.contact);

        if (!paymentMethodsResponse.Success) {
          return;
        }

        const paymentMethod = paymentMethodsResponse.PaymentMethods.find((method) => method.Token === state.token);

        if (!paymentMethod) {
          console.warn('Payment method not found for token:', state.token);

          return;
        }

        const paymentMethodChangeValues = await handlePaymentMethodChange({
          Token: state.token,
          PaymentMethod: paymentMethod.PaymentMethod,
          Value: state.token,
          Contact: state.contact,
        });

        setSelectedPaymentMethod({
          ...(paymentMethodChangeValues?.values || {}),
          Currency: paymentMethod.Currency || (Country === 'US' ? 'USD' : 'CAD'),
        });
      } catch (err) {
        if (!(err instanceof Error)) return;
        console.error('Error fetching payment method:', err);

        logMessage('Error fetching payment method', 'error', {
          feature: 'Single Collection',
          errorMessage: err.message,
          trace: err.stack,
        });

        alertDialog.error("Couldn't get payment method", err.message);
      } finally {
        blockingLoader.hide();
      }
    };

    fetchPaymentMethod();
  }, [state?.contact, state?.token]);

  const currentDate = useMemo(() => String(Date.now()), []);

  const fundDefaultValues: PaymentFieldDefault = {
    Currency: Country === 'US' ? 'USD' : 'CAD',
    Destination: 'balance',
    PaymentMethod: token,
    PaymentSelectionMethod: '',
    BankingPaymentRail: Country === 'CA' ? 'eft' : 'ach',
    Contact: contact,
    Amount: '',
    ScheduleType: 'single',
    Recurrence: 'one-time',
    TakeFromAccountOn: currentDate,
    Frequency: 'monthly',
    SemiMonthlyDates: '',
    StartDate: currentDate,
    Ending: 'End Date',
    EndDate: '',
    Description: '',
    Message: '',
    Notes: '',
    ClientAccount: '',
    TransactionLabel: '',
    MessageForRecipient: '',
    EndingAfterPayments: '',
    IdentificationNumber: '',
    ClientReferenceNumber: '',
    FirstName: '',
    LastName: '',
    Address1: '',
    Address2: '',
    City: '',
    Province: '',
    Country,
    PostalCode: '',
    FinancialInstitutionNumber: '',
    BranchTransitNumber: '',
    ABARoutingNumber: '',
    AccountNumber: '',
    ContactType: 'individual',
    CompanyName: '',
    EtransferConnectionType: 'email',
    RecipientName: '',
    RecipientPhoneNumber: '',
    RecipientEmailAddress: '',
    Question: '',
    Answer: '',
    Nickname: '',
    ProvideOrRequestPaymentInformation: 'provide',
    AccountConnectionMethod: 'Manual',
    InvoiceNumber: '',
    SendEmail: false,
    SenderName: AccountName,
    SendNotification: true,
    SendReminder: true,
    ReminderFrequency: '7',
    NotificationMethod: 'email',
    CancellationPeriod: 14, //default is 14 days
    SecCode: 'PPD',
    AcceptedCardBrands: allCardBrands.join(','),
    CardTypeValidation: false,
    BankPhoneNumber: '',
    ...selectedPaymentMethod,
  };

  const { createScheduledPayment } = useCreateScheduledPayment();
  const [, , , postEftFund] = usePostEftFund({});
  const [, , , postInteracMoneyRequest] = usePostInteracMoneyRequest({});
  const [, , , postACHFund] = usePostACHFund({});
  const [, , , postChargeCreditCard] = usePostChargeCreditCard({});
  const { withdrawMyAccount } = useWithdrawMyAccount();
  const { AFTPull } = useDebitCard();

  const { uploadDocument } = useUploadDocument();

  // TODO: encapsulate client account/contact behavior in to useCustomer
  const { createContact, editContact, fetchContact } = useContacts();
  const { createClientAccount, editClientAccount, fetchClientAccount } = useClientAccounts();

  const { addInterac } = usePaymentMethods();

  const CUSTOMER_ID_PROP = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const alertDialog = useAlertDialog();

  const [, , addBankAccount] = usePostIQ11Tokenize({});

  const [, , , generateEmbedUrl] = usePostEmbedUrl({});
  const { isDomesticCurrency } = usePaymentMethodCurrencySupport('Collect');
  const [, , , sendPaylink] = usePostSendPaylink({});

  const createPayment = async (values: PaymentFields) => {
    // TODO: Refactor into a factory function or strategy pattern to handle different PaymentSelectionMethods
    // This would reduce code duplication and make it easier to add new payment methods
    const isScheduled =
      values.ScheduleType === 'recurring' || (values.TakeFromAccountOn && !IsTodayDate(values.TakeFromAccountOn));

    const getScheduledPaymentRail = (): ScheduledPaymentRail | undefined => {
      if (values.PaymentSelectionMethod === 'bank') return values.BankingPaymentRail;
      if (values.PaymentSelectionMethod === 'email') return 'interac';
      if (values.PaymentSelectionMethod === 'credit') return 'creditcard';
      if (values.PaymentSelectionMethod === 'debitcard') return 'aft';

      return undefined;
    };

    // POST /scheduled-payment supports every rail
    const submitScheduledPayment = async (rail: ScheduledPaymentRail, currency: string) => {
      const response = await createScheduledPayment({
        Amount: values.Amount || 0,
        Currency: toSupportedCurrency(currency),
        PaymentType: 'debit',
        PaymentRail: rail,
        Token: values.PaymentMethod || '',
        StartDateTime: values.StartDate || values.TakeFromAccountOn || '',
        Repeats: values.Frequency && values.Frequency !== 'custom' ? values.Frequency : undefined,
        Interval: values.Interval,
        ByDay: values.ByDay,
        SemiMonthlyFrequencyType: values.Frequency === 'semi-monthly' ? values.SemiMonthlyDates || '' : undefined,
        EndDate: values.Ending === 'End Date' ? values.EndDate || undefined : undefined,
        NumberOfPayments:
          values.Ending === 'Payments' && values.EndingAfterPayments ? Number(values.EndingAfterPayments) : undefined,
        Description: values.Description || '',
        Notes: values.Notes || '',
        GLCode: values.GLCode || '',
        ClientAccountID: clientAccountsEnabled ? values.Contact : '',
        ClientReferenceNumber: values.ClientReferenceNumber || '',
        IdempotencyKey: '',
      });

      history.push(`/scheduled-transactions/details/${response.ScheduledPaymentsID}`);
    };

    const base = {
      Amount: values.Amount || 0,
      PortalUser: AccountID || '',
      GLCode: values.GLCode || '',
      TransactionTypeCode: values.TransactionCode || '',
      ClientReferenceNumber: values.ClientReferenceNumber || '',
      TransactionLabel: values.TransactionLabel || '',
      [CUSTOMER_ID_PROP]: clientAccountsEnabled ? values.Contact : '',
    };

    const common = {
      FirstName: values.FirstName,
      LastName: values.LastName,
      CompanyName: values.CompanyName,
      PhoneNumber: values.RecipientPhoneNumber,
      Address1: values.Address1 || '',
      City: values.City || '',
      Province: values.Province || '',
      Country: Country || '',
      PostalCode: values.PostalCode || '',
      Token: clientAccountsEnabled ? '' : values.PaymentMethod,

      ...base,
    };

    const withdrawToBank = async (collectionTransactionID: string) => {
      if (values.Destination !== 'bank') return;

      try {
        await withdrawMyAccount({
          Token: '',
          Amount: values.Amount || 0,
          Currency: toSupportedCurrency(values.Currency),
          ClientReferenceNumber: values.ClientReferenceNumber || '',
          ParentTransactionID: collectionTransactionID,
          // Marks this as an auto-sweep, not a funded payment, despite the same
          // ParentTransactionID shape — TransactionDetails uses it to suppress the funding banners.
          Notes: 'Deposit Collected Funds',
        });
      } catch (withdrawError) {
        logMessage('Error withdrawing collected funds to bank', 'error', {
          feature: 'Single Collection',
          errorMessage: withdrawError instanceof Error ? withdrawError.message : String(withdrawError),
          trace: withdrawError instanceof Error ? withdrawError.stack : undefined,
        });

        alertDialog.caution(
          t('withdrawToBankFailedTitle', { defaultValue: 'Funds collected, but transfer to bank failed' }),
          withdrawError instanceof Error ? withdrawError.message : String(withdrawError),
          { abortLabel: t('close') },
        );
      }
    };

    try {
      blockingLoader.show();

      if (isScheduled) {
        const rail = getScheduledPaymentRail();

        if (!rail) {
          throw new Error('Unable to determine the payment rail for this scheduled collection.');
        }

        await submitScheduledPayment(rail, values.Currency || (Country === 'CA' ? 'CAD' : 'USD'));
      } else if (values.PaymentSelectionMethod === 'bank') {
        if (values.BankingPaymentRail === 'eft') {
          const eftWithdrawResponse = await postEftFund({
            ...common,
            Currency: values.Currency || '',
            Notes: values.Notes || '',
          });

          if (!eftWithdrawResponse.Success) {
            throw new Error(eftWithdrawResponse.ErrorMessage);
          }

          await withdrawToBank(eftWithdrawResponse.TransactionID);

          const queryParams = new URLSearchParams({
            ContactID: values.Contact || '',
            token: values.PaymentMethod || '',
          });

          history.push(
            `/transaction-confirmation/transaction/collection/EFTFunding/${eftWithdrawResponse.TransactionID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
          );
        }

        if (values.BankingPaymentRail === 'ach') {
          const response = await postACHFund({
            ...common,
            Currency: values.Currency || 'USD',
            PhoneNumber: values.RecipientPhoneNumber || common?.PhoneNumber || values.NewPhoneNumber || '',
            State: values.Province || '',
            ZipCode: values.PostalCode || '',
            IdentificationNumber: values.IdentificationNumber || '',
            EmailAddress: values.RecipientEmailAddress || '',
            SecCode: values.SecCode || 'PPD',
          });

          if (!response.Success) {
            throw new Error(response.ErrorMessage);
          }

          await withdrawToBank(response.TransactionID);

          const queryParams = new URLSearchParams({
            ContactID: values.Contact || '',
            token: values.PaymentMethod || '',
          });

          history.push(
            `/transaction-confirmation/transaction/collection/ACHFunding/${response.TransactionID}/${values.Currency || 'USD'}?${queryParams.toString()}`,
          );
        }
      } else if (values.PaymentSelectionMethod === 'email') {
        const response = await postInteracMoneyRequest({
          Currency: values.Currency || '',
          EmailAddress:
            !clientAccountsEnabled && values.EtransferConnectionType === 'email'
              ? values.RecipientEmailAddress || ''
              : '',
          PhoneNumber:
            !clientAccountsEnabled && values.EtransferConnectionType === 'phone' ? values.RecipientPhoneNumber : '',
          RecipientName: values.RecipientName || '',
          Notes: values.Notes || '',
          GenerateURL: false,
          MessageForRecipient: values.Message || '',
          ...base,
        });

        if (!response.Success) {
          throw new Error(response.ErrorMessage);
        }

        const queryParams = new URLSearchParams({
          ContactID: values.Contact || '',
          token: values.PaymentMethod || '',
        });

        history.push(
          `/transaction-confirmation/transaction/collection/InteracMoneyRequest/${response.TransactionID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
        );
      } else if (values.PaymentSelectionMethod === 'debitcard') {
        const response = await AFTPull({
          Currency: values.Currency || '',
          CardToken: clientAccountsEnabled ? '' : values.PaymentMethod || '',
          Notes: values.Notes || '',
          ...base,
        });

        if (!response.Success) {
          throw new Error(response.ErrorMessage);
        }

        await withdrawToBank(response.TransactionID);

        const queryParams = new URLSearchParams({
          ContactID: values.Contact || '',
          token: values.PaymentMethod || '',
        });

        history.push(
          `/transaction-confirmation/transaction/collection/AFTPull/${response.TransactionID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
        );
      } else if (values.PaymentSelectionMethod === 'credit') {
        const response = await postChargeCreditCard({
          Currency: values.Currency || '',
          Country: Country || '',
          CreditCardToken: clientAccountsEnabled ? '' : values.PaymentMethod || '',
          ...base,
        });

        if (!response.Success) {
          throw new Error(response.ErrorMessage);
        }

        await withdrawToBank(response.TransactionID);

        const queryParams = new URLSearchParams({
          ContactID: values.Contact || '',
          token: values.PaymentMethod || '',
        });

        history.push(
          `/transaction-confirmation/transaction/collection/CreditCard/${response.TransactionID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
        );
      }
    } catch (error) {
      console.error('Error creating payment:', error);

      logMessage('Error creating payment', 'error', {
        feature: 'Single Collection',
        errorMessage: error instanceof Error ? error.message : String(error),
        trace: error instanceof Error ? error.stack : undefined,
        formValues: {
          values,
          base,
          common,
        },
      });

      alertDialog.error('Error', error instanceof Error ? error.message : String(error), {
        abortLabel: t('close'),
      });
    } finally {
      blockingLoader.hide();
    }
  };

  const handleSubmit = async (values: PaymentFields) => {
    const isNewContact = !values.Contact || values.Contact === 'new';

    const isNewPaymentMethod =
      isNewContact || !values.PaymentMethod || values.PaymentMethod === 'new' || values.PaymentMethod === 'request';

    const isRequest = values.ProvideOrRequestPaymentInformation === 'request';
    let contactClientAccountID = values.Contact;

    try {
      blockingLoader.show();

      if (isNewContact) {
        if (clientAccountsEnabled) {
          const res = await createClientAccount(
            {
              FirstName: values.FirstName,
              LastName: values.LastName,
              EmailAddress: values.NewEmailAddress,
              PhoneNumber: values.NewPhoneNumber,
              BusinessName: values.CompanyName,
              Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
              City: values.City,
              Province: values.Province,
              PostalCode: values.PostalCode,
              Country: values.Country,
              Currency: Country === 'CA' ? 'CAD' : 'USD',
              IdentificationNumber: values.IdentificationNumber,
            },
            false,
          );

          contactClientAccountID = res.clientAccountID;
          values.RecipientName = values.CompanyName || `${values.FirstName} ${values.LastName}`;
        } else {
          const res = await createContact({
            FirstName: values.FirstName,
            LastName: values.LastName,
            //Default email and phone number are required for creating contact because some payment methods require them
            EmailAddress:
              values.RecipientEmailAddress || values.ReceiverEmailAddress || 'account@clientaccount.vopay.com',
            PhoneNumber: values.ReceiverPhoneNumber || values.RecipientPhoneNumber || '0000000000',
            BusinessName: values.CompanyName,
            Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
            City: values.City,
            Province: values.Province,
            PostalCode: values.PostalCode,
            Country: values.Country,
          });

          contactClientAccountID = res.contactID;
          values.RecipientName = values.CompanyName || `${values.FirstName} ${values.LastName}`;
        }
      } else if (contactClientAccountID) {
        // Update existing contact/client account with address if address fields are filled

        const hasAddressData =
          values.Address1 &&
          values.Address1.trim() !== '' &&
          values.City &&
          values.City.trim() !== '' &&
          values.Province &&
          values.Province.trim() !== '' &&
          values.PostalCode &&
          values.PostalCode.trim() !== '';

        let address1 = '';
        let city = '';
        let province = '';
        let postalCode = '';
        let phoneNumber = '';

        if (clientAccountsEnabled) {
          const account = await fetchClientAccount(contactClientAccountID);

          if (account) {
            address1 = account.Address1 || '';
            city = account.City || '';
            province = account.Province || '';
            postalCode = account.PostalCode || '';
            phoneNumber = account.PhoneNumber || '';
          }
        } else {
          const contact = await fetchContact(contactClientAccountID);

          if (contact) {
            address1 = contact.Address1 || '';
            city = contact.City || '';
            province = contact.Province || '';
            postalCode = contact.PostalCode || '';
            phoneNumber = contact.PhoneNumber || '';
          }
        }

        const isAddressChanged =
          values.Address1 + (values.Address2 ? `, ${values.Address2}` : '') !== address1 ||
          values.City !== city ||
          values.Province !== province ||
          values.PostalCode !== postalCode;

        const hasPhoneData =
          values.RecipientPhoneNumber &&
          values.RecipientPhoneNumber.trim() !== '' &&
          values.RecipientPhoneNumber !== '0000000000';

        const isPhoneChanged = hasPhoneData && values.RecipientPhoneNumber !== phoneNumber;

        if ((hasAddressData && isAddressChanged) || isPhoneChanged) {
          if (clientAccountsEnabled) {
            await editClientAccount({
              ClientAccountID: contactClientAccountID,
              ...(hasAddressData &&
                isAddressChanged && {
                  Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
                  City: values.City,
                  Province: values.Province,
                  PostalCode: values.PostalCode,
                  Country: values.Country,
                }),
              ...(isPhoneChanged && {
                PhoneNumber: values.RecipientPhoneNumber,
              }),
            });
          } else {
            await editContact({
              ContactID: contactClientAccountID,
              ...(hasAddressData &&
                isAddressChanged && {
                  Address1: values.Address1 + (values.Address2 ? `, ${values.Address2}` : ''),
                  City: values.City,
                  Province: values.Province,
                  PostalCode: values.PostalCode,
                  Country: values.Country,
                }),
              ...(isPhoneChanged && {
                PhoneNumber: values.RecipientPhoneNumber,
              }),
            });
          }
        }
      }

      if (isNewPaymentMethod) {
        if (isRequest) {
          //TODO: modify to handle payments
          handleRequestPaymentMethod(values, contactClientAccountID || ''); // Pass the account
        } else {
          const token = await handleAddPaymentMethod(values, contactClientAccountID || ''); // Pass the account ID or contact client account ID
          if (!token) return;

          createPayment({
            ...values,
            Contact: contactClientAccountID || '',
            PaymentMethod: token || '',
          });
        }
      } else {
        createPayment({
          ...values,
          Contact: contactClientAccountID || '',
        });
      }
    } catch (error) {
      console.error('Error handling submit:', error);

      logMessage('Error handling submit', 'error', {
        feature: 'Single Collection',
        errorMessage: error instanceof Error ? error.message : String(error),
        trace: error instanceof Error ? error.stack : undefined,
        formValues: values,
      });

      alertDialog.error('Error', error instanceof Error ? error.message : String(error), {
        abortLabel: t('close'),
      });
    } finally {
      blockingLoader.hide();
    }
  };

  async function handleAddPaymentMethod(values: PaymentFields, accountID: string): Promise<string | undefined> {
    if (values.PaymentSelectionMethod === 'bank') {
      return await handleAddBankAccount(values, accountID);
    } else if (values.PaymentSelectionMethod === 'email') {
      return await handleAddInterac(values, accountID);
    } else if (values.PaymentSelectionMethod === 'credit') {
      return await openELinxPaymentMethod('credit', accountID);
    } else if (values.PaymentSelectionMethod === 'debitcard') {
      return await openELinxPaymentMethod('debitcard', accountID);
    }

    return undefined;
  }

  // eLinx opens an interactive overlay the user must complete, so the submit's blocking loader
  // comes down while open() is pending. The finally re-raise (open() rejects when the user closes
  // the overlay) keeps the loader counter balanced with the submit handler's own show/hide pair.
  async function openELinxPaymentMethod(
    paymentSelectionMethod: 'credit' | 'debitcard',
    accountID: string,
  ): Promise<string> {
    blockingLoader.hide();

    try {
      // open() resolves the whole link payload; a single-method open already knows what was linked, so
      // only the token matters here.
      const { Token } = await eLinxConnect.open(
        {
          PaymentSelectionMethod: paymentSelectionMethod,
          ClientControlled: clientAccountsEnabled,
        },
        accountID,
      );

      return Token;
    } finally {
      blockingLoader.show();
    }
  }

  async function handleAddInterac(values: PaymentFields, accountID: string): Promise<string | undefined> {
    try {
      blockingLoader.show();

      const res = await addInterac({
        accountID,
        recipientName: values.RecipientName,
        email: values.RecipientEmailAddress,
        phone: values.RecipientPhoneNumber,
        question: values.Question,
        answer: values.Answer,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res.Token;
    } catch (err) {
      console.error(err);

      if (err instanceof Error) {
        console.error('Error adding interac:', err);

        logMessage('Error adding interac', 'error', {
          feature: 'Single Collection',
          errorMessage: err instanceof Error ? err.message : String(err),
          trace: err instanceof Error ? err.stack : undefined,
          formValues: {
            accountID,
            ...values,
          },
        });

        alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    } finally {
      blockingLoader.hide();
    }
  }

  async function handleAddBankAccount(values: PaymentFields, accountID: string): Promise<string | undefined> {
    try {
      blockingLoader.show();
      const accountHolderName = values.CompanyName ? values.CompanyName : `${values.FirstName} ${values.LastName}`;

      const bankRes = await addBankAccount({
        PortalUser: LoginUserName,
        //TODO add company name some how
        AccountHolderName: accountHolderName,
        Currency: values.Currency || (Country === 'CA' ? 'CAD' : 'USD'),
        AccountNumber: values.AccountNumber,
        FinancialInstitutionNumber: values.FinancialInstitutionNumber,
        BranchTransitNumber: values.BranchTransitNumber,
        ABARoutingNumber: values.ABARoutingNumber, // for US account
        ClientControlled: clientAccountsEnabled,
        [CUSTOMER_ID_PROP]: accountID,
        Address1: values.Address1,
        City: values.City,
        Province: values.Province,
        PostalCode: values.PostalCode,
        PhoneNumber: values.RecipientPhoneNumber || values.NewPhoneNumber,
        Country: Country,
      });

      if (!bankRes.Success) {
        throw new Error(bankRes.ErrorMessage);
      }

      return bankRes.Token;
    } catch (err) {
      logMessage('Error adding bank account', 'error', {
        feature: 'Single Collection',
        errorMessage: err instanceof Error ? err.message : String(err),
        trace: err instanceof Error ? err.stack : undefined,
        formValues: {
          accountID,
          ...values,
        },
      });

      console.error(err);

      if (err instanceof Error) {
        await alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    } finally {
      blockingLoader.hide();
    }
  }

  async function handleRequestPaymentMethod(values: PaymentFields, accountID: string) {
    let documentID = '';

    try {
      blockingLoader.show();

      // earthnode (ID-13659) rejects an IframeKey on a request in the account's other currency — the
      // iframe row's methods are shared by every eLinx made with it, and the currency must narrow
      // them — and builds the hosted page itself instead. The Card Settings' brands still reach it
      // through the request body; the method and account-selection choices do not.
      let iframeKey: string | undefined;

      if (isDomesticCurrency(values.Currency)) {
        const embedUrlRes = await generateEmbedUrl({
          RedirectURL: 'https://request-dev.vopay.com/',
          Language: 'en',
          RedirectMethod: 'JavascriptMessage',
          PaymentSelectionMethod: values.PaymentSelectionMethod,
          AccountSelectionMethod: values.AccountSelectionMethod,
          ClientControlled: clientAccountsEnabled,
          [CUSTOMER_ID_PROP]: accountID,
          ClientReferenceNumber: '',
          TermsAndConditions: '',
          Country: Country,
          AcceptedCardBrands: values.AcceptedCardBrands,
          CardTypeValidation: values.CardTypeValidation,
        });

        if (!embedUrlRes.Success) {
          throw new Error(embedUrlRes.ErrorMessage);
        }

        iframeKey = embedUrlRes.IframeKey;
      }

      if (values.InvoiceFileName && values.InvoiceFile) {
        const documentUploadRes = await uploadDocument({
          DocumentContent: values.InvoiceFile,
          DocumentType: 'invoice',
          DocumentName: values.InvoiceFileName,
        });

        if (!documentUploadRes.Success) {
          throw new Error(documentUploadRes.ErrorMessage);
        }

        documentID = documentUploadRes.DocumentID;
      }

      const receiverName = values.CompanyName ? values.CompanyName : `${values.FirstName} ${values.LastName}`;

      // A future TakeFromAccountOn makes a one-time request scheduled (but non-recurring) too —
      // not just an explicit Recurrence preset. `values` here is already the serialized payload
      // (see this form's `onSubmit`), so TakeFromAccountOn is already a 'yyyy-MM-dd' string.
      const isScheduledRequest =
        values.ScheduleType === 'recurring' || (!!values.TakeFromAccountOn && !IsTodayDate(values.TakeFromAccountOn));

      const sendPaylinkRes = await sendPaylink({
        Amount: values.Amount || 0,
        Currency: values.Currency || (Country === 'CA' ? 'CAD' : 'USD'),
        SenderName: values.SenderName || AccountName || '',
        ReceiverName: receiverName,
        ReceiverEmailAddress: values.ReceiverEmailAddress,
        ReceiverPhoneNumber: values.ReceiverPhoneNumber,
        IframeKey: iframeKey,
        AcceptedCardBrands: values.AcceptedCardBrands,
        [CUSTOMER_ID_PROP]: accountID,
        SendNotification: !!values.SendNotification,
        NotificationMethod: values.NotificationMethod,
        PaymentType: 'fund',
        InvoiceNumber: values.InvoiceNumber,
        Note: values.Notes,
        ClientReferenceNumber: values.ClientReferenceNumber,
        SendEmail: values.SendEmail,
        DocumentID: documentID,
        PaymentMethodDisplayOptions: values.PaymentMethodDisplayOptions,
        SemiMonthlyFrequencyType: values.SemiMonthlyDates || '',
        Frequency: isScheduledRequest ? (values.Frequency ? 'recurring' : 'single') : '',
        NameOfFrequency: values.Frequency,
        Interval: values.Interval,
        ByDay: values.ByDay,
        // StartDate only exists for recurring presets — a one-time scheduled request carries its
        // future date on TakeFromAccountOn instead. The backend rejects ScheduleStartDate outright
        // unless it's tomorrow+, so a same-day (unscheduled) one-time request must omit it, same
        // as Frequency above — gate on isScheduledRequest rather than sending it unconditionally.
        ScheduleStartDate: values.StartDate || (isScheduledRequest ? values.TakeFromAccountOn : '') || '',
        ScheduleEndDate: values.EndDate || '',
        EndingAfterPayments: values.EndingAfterPayments || '',
        SendReminder: values.SendReminder,
        ReminderFrequency: values.ReminderFrequency,
        RecipientMessage: values.RecipientMessage,
        BankingPaymentRail: values.PaymentSelectionMethod?.includes('bank') ? values.BankingPaymentRail : undefined,
      });

      if (!sendPaylinkRes.Success) {
        throw new Error(sendPaylinkRes.ErrorMessage);
      }

      // Navigate to eLinx confirmation page
      const notificationMethod = values.SendNotification
        ? values.NotificationMethod?.toLowerCase?.() || 'none'
        : 'none';

      const queryParams = new URLSearchParams({
        notificationMethod,
        ContactID: accountID,
        scheduled: isScheduledRequest.toString(),
      });

      history.push(
        `/transaction-confirmation/elinx/collection/eCollect/${sendPaylinkRes.ELinxRequestID}/${values.Currency || 'CAD'}?${queryParams.toString()}`,
      );
    } catch (err) {
      if (err instanceof Error) {
        console.error('Error requesting payment:', err);

        logMessage('Error  requesting payment', 'error', {
          feature: 'Single Collection',
          errorMessage: err instanceof Error ? err.message : String(err),
          trace: err instanceof Error ? err.stack : undefined,
          formValues: {
            accountID,
            documentID,
            ...values,
          },
        });

        alertDialog.error('Error', err.message, {
          abortLabel: t('close'),
        });
      }
    } finally {
      blockingLoader.hide();
    }
  }

  return (
    <div className={classes('pageContainer')}>
      <div className={classes('stickyHeader')}>
        <Box flx="row" jc="center">
          <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
            {state && (
              <UtilButton
                iconLeft="chevron_left"
                size="large"
                onClick={() => {
                  history.goBack();
                }}
              >
                {tGlobal('Buttons.back')}
              </UtilButton>
            )}

            <Title>
              <Box ai="center" gapSize="4px" jc="sb">
                {t('pageTitle')}
              </Box>
            </Title>
          </Box>
        </Box>
      </div>

      <Box flx="row" jc="center">
        <Box flx="col" jc="center" maxwidth="1121px" boxWidth="100%" p="md">
          <Box flx="col" gap="md">
            {selectedPaymentMethod && (
              <SingleCollectionForm defaultValues={fundDefaultValues} onSubmit={handleSubmit} />
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
};

export default SingleCollectionContainer;
